import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './infoChild.module.scss'
import UserInput from '@/components/_custom/UserInput/userInput'
import Title from '@/components/_custom/Title/title'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import { CREATE_CHILD, UPDATE_CHILD } from '@/modules/API/parent/childHandler'
import { useMutation } from '@apollo/client'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Child } from '@/types/Child'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

// No-op function used as a placeholder callback when signUpAutolink is true
const noop: () => void = (): void => undefined

interface InfoChildDialogProps {
    open: boolean
    handleClose: () => void
    child: Child | null
    getParent: () => void
    signUpAutolink: boolean
    isBelgian: boolean
}

const InfoChildDialog: FunctionComponent<InfoChildDialogProps> = ({
    open,
    handleClose,
    child,
    getParent,
    signUpAutolink,
    isBelgian
}: InfoChildDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [firstname, setFirstname] = useState<string>(child?.firstname || '')
    const [birthdate, setBirthdate] = useState<string>(child?.birthdate || '')
    const [preRegistrationBelgium, setPreRegistrationBelgium] = useState<string>(
        (isBelgian && child?.preRegistrationBelgium) || ''
    )

    const [monMorning, setMonMorning] = useState<boolean>(child ? child?.mon % 2 !== 0 : false)
    const [tueMorning, setTueMorning] = useState<boolean>(child ? child?.tue % 2 !== 0 : false)
    const [wedMorning, setWedMorning] = useState<boolean>(child ? child?.wed % 2 !== 0 : false)
    const [thuMorning, setThuMorning] = useState<boolean>(child ? child?.thu % 2 !== 0 : false)
    const [friMorning, setFriMorning] = useState<boolean>(child ? child?.fri % 2 !== 0 : false)
    const [satMorning, setSatMorning] = useState<boolean>(child ? child?.sat % 2 !== 0 : false)
    const [sunMorning, setSunMorning] = useState<boolean>(child ? child?.sun % 2 !== 0 : false)

    const [monAfternoon, setMonAfternoon] = useState<boolean>(child ? child?.mon >= 2 : false)
    const [tueAfternoon, setTueAfternoon] = useState<boolean>(child ? child?.tue >= 2 : false)
    const [wedAfternoon, setWedAfternoon] = useState<boolean>(child ? child?.wed >= 2 : false)
    const [thuAfternoon, setThuAfternoon] = useState<boolean>(child ? child?.thu >= 2 : false)
    const [friAfternoon, setFriAfternoon] = useState<boolean>(child ? child?.fri >= 2 : false)
    const [satAfternoon, setSatAfternoon] = useState<boolean>(child ? child?.sat >= 2 : false)
    const [sunAfternoon, setSunAfternoon] = useState<boolean>(child ? child?.sun >= 2 : false)

    async function handleCloseDialog(): Promise<void> {
        if (child) {
            await updateChild()
        } else {
            await createChild()
        }
    }

    const [createChild, { loading: loadingCreate }] = useMutation(CREATE_CHILD, {
        variables: {
            firstname: firstname,
            birthdate: birthdate,
            preRegistrationBelgium: preRegistrationBelgium,
            mon: Number(monMorning) + Number(monAfternoon) + Number(monAfternoon),
            tue: Number(tueMorning) + Number(tueAfternoon) + Number(tueAfternoon),
            wed: Number(wedMorning) + Number(wedAfternoon) + Number(wedAfternoon),
            thu: Number(thuMorning) + Number(thuAfternoon) + Number(thuAfternoon),
            fri: Number(friMorning) + Number(friAfternoon) + Number(friAfternoon),
            sat: Number(satMorning) + Number(satAfternoon) + Number(satAfternoon),
            sun: Number(sunMorning) + Number(sunAfternoon) + Number(sunAfternoon)
        },
        onCompleted: () => {
            setFirstname('')
            setBirthdate('')
            setPreRegistrationBelgium('')
            showNotificationSuccess(t('childAdded'))
            completed()
        },
        onError: () => error()
    })

    const [updateChild, { loading: loadingUpdate }] = useMutation(UPDATE_CHILD, {
        variables: {
            child_id: child?.id,
            firstname: firstname,
            birthdate: birthdate,
            preRegistrationBelgium: preRegistrationBelgium,
            mon: Number(monMorning) + Number(monAfternoon) + Number(monAfternoon),
            tue: Number(tueMorning) + Number(tueAfternoon) + Number(tueAfternoon),
            wed: Number(wedMorning) + Number(wedAfternoon) + Number(wedAfternoon),
            thu: Number(thuMorning) + Number(thuAfternoon) + Number(thuAfternoon),
            fri: Number(friMorning) + Number(friAfternoon) + Number(friAfternoon),
            sat: Number(satMorning) + Number(satAfternoon) + Number(satAfternoon),
            sun: Number(sunMorning) + Number(sunAfternoon) + Number(sunAfternoon)
        },
        onCompleted: () => {
            showNotificationSuccess(t('childUpdated'))
            completed()
        },
        onError: () => error()
    })

    function completed(): void {
        getParent()
        handleClose()
    }

    function error(): void {
        showNotificationError(t('generalError'))
    }

    const disableButton: boolean =
        firstname === '' ||
        birthdate === '' ||
        (child?.firstname === firstname &&
            child?.birthdate === birthdate &&
            child.preRegistrationBelgium === preRegistrationBelgium &&
            child?.mon === Number(monMorning) + Number(monAfternoon) + Number(monAfternoon) &&
            child?.tue === Number(tueMorning) + Number(tueAfternoon) + Number(tueAfternoon) &&
            child?.wed === Number(wedMorning) + Number(wedAfternoon) + Number(wedAfternoon) &&
            child?.thu === Number(thuMorning) + Number(thuAfternoon) + Number(thuAfternoon) &&
            child?.fri === Number(friMorning) + Number(friAfternoon) + Number(friAfternoon) &&
            child?.sat === Number(satMorning) + Number(satAfternoon) + Number(satAfternoon) &&
            child?.sun === Number(sunMorning) + Number(sunAfternoon) + Number(sunAfternoon)) ||
        loadingCreate ||
        loadingUpdate

    return (
        <Dialog open={open} handleClose={signUpAutolink ? noop : handleClose}>
            <DialogHeader title={child ? t('modifyChild') : t('addChild')} parent />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.Form}>
                    <Title text={t('informations')} className={styles.Title} />
                    <div className={styles.InputContainer}>
                        <UserInput
                            label={t('firstname')}
                            value={firstname}
                            fullWidth
                            parent
                            required
                            className={styles.Input}
                            setValue={setFirstname}
                            format={'pascal'}
                            type={'text'}
                        />
                        <DatePickerComponent
                            label={t('birthdate')}
                            value={birthdate}
                            setValue={setBirthdate}
                            parent
                            disablePast={false}
                            className={styles.Input}
                            allowEmpty
                        />
                    </div>
                    {isBelgian && (
                        <div className={styles.InputContainer}>
                            <UserInput
                                label={t('preRegistration')}
                                value={preRegistrationBelgium}
                                fullWidth
                                parent
                                className={styles.Input}
                                setValue={setPreRegistrationBelgium}
                                format={'pascal'}
                                type={'text'}
                            />
                        </div>
                    )}
                    <div className={styles.BreakLine}></div>
                    <Title text={t('preferences')} className={styles.Title} />
                    <div className={styles.InputContainer}>
                        <TableContainer className={styles.TablePlacement}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell></TableCell>
                                        <TableCell align={'center'} className={styles.TableHeader}>
                                            {t('morning')}
                                        </TableCell>
                                        <TableCell align={'center'} className={styles.TableHeader}>
                                            {t('afternoon')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <DayRow
                                        day={t('monday')}
                                        morning={monMorning}
                                        afternoon={monAfternoon}
                                        setMorning={setMonMorning}
                                        setAfternoon={setMonAfternoon}
                                    />
                                    <DayRow
                                        day={t('tuesday')}
                                        morning={tueMorning}
                                        afternoon={tueAfternoon}
                                        setMorning={setTueMorning}
                                        setAfternoon={setTueAfternoon}
                                    />
                                    <DayRow
                                        day={t('wednesday')}
                                        morning={wedMorning}
                                        afternoon={wedAfternoon}
                                        setMorning={setWedMorning}
                                        setAfternoon={setWedAfternoon}
                                    />
                                    <DayRow
                                        day={t('thursday')}
                                        morning={thuMorning}
                                        afternoon={thuAfternoon}
                                        setMorning={setThuMorning}
                                        setAfternoon={setThuAfternoon}
                                    />
                                    <DayRow
                                        day={t('friday')}
                                        morning={friMorning}
                                        afternoon={friAfternoon}
                                        setMorning={setFriMorning}
                                        setAfternoon={setFriAfternoon}
                                    />
                                    <DayRow
                                        day={t('saturday')}
                                        morning={satMorning}
                                        afternoon={satAfternoon}
                                        setMorning={setSatMorning}
                                        setAfternoon={setSatAfternoon}
                                    />
                                    <DayRow
                                        day={t('sunday')}
                                        morning={sunMorning}
                                        afternoon={sunAfternoon}
                                        setMorning={setSunMorning}
                                        setAfternoon={setSunAfternoon}
                                    />
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                </div>
                <div className={styles.TextContainer}></div>
                <div className={styles.ButtonContainer}>
                    {!signUpAutolink && (
                        <ButtonTextIcon
                            type={'close'}
                            onClick={handleClose}
                            className={styles.Button}
                            parent
                            fullWidth
                        />
                    )}
                    <ButtonTextIcon
                        type={child ? 'update' : 'confirm'}
                        onClick={handleCloseDialog}
                        className={styles.Button}
                        parent
                        fullWidth
                        disabled={disableButton}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

interface DayRowProps {
    day: string
    morning: boolean
    afternoon: boolean
    setMorning: (value: boolean) => void
    setAfternoon: (value: boolean) => void
}

const DayRow: FunctionComponent<DayRowProps> = ({ day, morning, afternoon, setMorning, setAfternoon }: DayRowProps) => {
    return (
        <TableRow className={styles.DayRow}>
            <TableCell align={'left'} padding={'checkbox'} className={styles.DayCell}>
                {day}
            </TableCell>
            <TableCell align={'center'} padding={'checkbox'}>
                <Checkbox checked={morning} parent onChange={() => setMorning(!morning)} className={styles.Checkbox} />
            </TableCell>
            <TableCell align={'center'} padding={'checkbox'}>
                <Checkbox
                    checked={afternoon}
                    parent
                    onChange={() => setAfternoon(!afternoon)}
                    className={styles.Checkbox}
                />
            </TableCell>
        </TableRow>
    )
}

export default InfoChildDialog
